import { Children, isValidElement, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FilterIcon, XmarkIcon } from '@dynatrace/strato-icons';

import * as styles from './FilterCompositionMenuGroup.sty.js';
import type { FilterCompositionMenuGroupProps } from './FilterCompositionMenuGroup.types.js';
import { filterCompositionMenuGroupMessages as messages } from './messages.js';
import { operatorMessages } from './operator-messages.js';
import { useFilterFieldValueOf } from './useFilterFieldValueOf.js';
import { useAriaLabelingProps } from '../../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { devLog } from '../../../core/utils/logging.js';
import { Content } from '../../../navigation/menu/Content.js';
import { Group } from '../../../navigation/menu/Group.js';
import { Item } from '../../../navigation/menu/Item.js';
import { ItemTooltip } from '../../../navigation/menu/ItemTooltip.js';
import { Label } from '../../../navigation/menu/Label.js';
import { Prefix } from '../../../navigation/menu/Prefix.js';
import { Sub } from '../../../navigation/menu/Sub.js';
import { SubContent } from '../../../navigation/menu/SubContent.js';
import { SubTrigger } from '../../../navigation/menu/SubTrigger.js';
import { Suffix } from '../../../navigation/menu/Suffix.js';
import { Trigger } from '../../../navigation/menu/Trigger.js';
import { FilterStatement } from '../../filter-field/components/FilterStatement.js';
import type { FilterFieldProps } from '../../filter-field/FilterField.types.js';
import type {
  FilterFieldComparisonOperators,
  FilterFieldPrimitive,
} from '../../filter-field/types/validation.js';
import {
  getAllowedOperatorsForType,
  getOperatorShortName,
} from '../../filter-field/utils/validation-utils/operator-specification.js';
import { addFilter } from '../../filter-field/utils/value-operations/add-filter.js';
import type { FilterFieldStatementMatch } from '../../filter-field/utils/value-operations/get-filters.js';
import { getFilters } from '../../filter-field/utils/value-operations/get-filters.js';
import { toComparableScalar } from '../../filter-field/utils/value-operations/value-operation-utils.js';
import type { FilterCompositionStore } from '../store/filterCompositionStore.types.js';
import { useFilterCompositionById } from '../store/useFilterCompositionById.js';

const DISPLAY_NAME = 'FilterCompositionMenuGroup';

/**
 * The operators a key can be filtered by. `search` is keyless — it produces
 * `* ~ value`, targets no key, and has no operator name — so it is never offered.
 * `matches-wildcard` / `not-matches-wildcard` are excluded too: the menu offers
 * equality, presence, and phrase matching; wildcard matching overlaps with
 * `contains` and is not part of the item set.
 */
type OfferedOperator = Exclude<
  FilterFieldComparisonOperators,
  'search' | 'matches-wildcard' | 'not-matches-wildcard'
>;

function isOfferedOperator(
  operator: FilterFieldComparisonOperators,
): operator is OfferedOperator {
  return (
    operator !== 'search' &&
    operator !== 'matches-wildcard' &&
    operator !== 'not-matches-wildcard'
  );
}

/** The wildcard the existence operators filter against. */
const EXISTENCE_VALUE = '*';

/**
 * Operators that are the plain negation of one another. Applying both to the same
 * value yields a filter nothing can match, so an item whose opposite is already
 * applied is offered as disabled rather than clickable.
 *
 * `equals`/`in` form one positive family and `not-equals`/`not in` one negative
 * family (see {@link EQUIVALENT_STORAGE_OPERATORS}), so the list operators point
 * at a representative of the opposite family and the lookup expands it back out.
 *
 * Ordering operators are deliberately absent. `> 5` and `<= 5` are mutually
 * exclusive too, but treating them as conflicts means reasoning about ranges —
 * which neither the FilterField nor the Experience Standard defines — and it
 * would disable the second half of a range a consumer is deliberately building.
 * Everything else is additive: `contains "a"` and `contains "b"` coexist.
 */
const CONFLICTING_OPERATOR: Partial<Record<OfferedOperator, OfferedOperator>> =
  {
    equals: 'not-equals',
    in: 'not-equals',
    'not-equals': 'equals',
    'not in': 'equals',
    exists: 'not-exists',
    'not-exists': 'exists',
    contains: 'not-contains',
    'not-contains': 'contains',
    'starts-with': 'not-starts-with',
    'not-starts-with': 'starts-with',
    'ends-with': 'not-ends-with',
    'not-ends-with': 'ends-with',
    'matches-phrase': 'not-matches-phrase',
    'not-matches-phrase': 'matches-phrase',
  };

/**
 * `addFilter` merges `key = a` and `key = b` into `key in (a, b)`, so a value
 * added as `equals` may end up stored under either operator. The same holds for
 * the negative pair.
 */
const EQUIVALENT_STORAGE_OPERATORS: Partial<
  Record<OfferedOperator, FilterFieldComparisonOperators[]>
> = {
  equals: ['equals', 'in'],
  in: ['equals', 'in'],
  'not-equals': ['not-equals', 'not in'],
  'not in': ['not-equals', 'not in'],
};

function isStatementPresent(
  matches: FilterFieldStatementMatch[],
  operator: OfferedOperator,
  comparableValue: string | number | boolean,
): boolean {
  if (operator === 'exists' || operator === 'not-exists') {
    return matches.some((match) => match.comparisonOperator === operator);
  }

  const storageOperators: FilterFieldComparisonOperators[] =
    EQUIVALENT_STORAGE_OPERATORS[operator] ?? [operator];

  // Both sides are already lowered into the same typed form — `matches` comes
  // from a `getFilters` call carrying the same `filterType`, `comparableValue`
  // from `toComparableScalar` — so a plain `===` matches exactly what
  // `addFilter` would deduplicate against.
  return matches.some(
    (match) =>
      storageOperators.includes(match.comparisonOperator) &&
      match.values.includes(comparableValue),
  );
}

/**
 * `Menu` container parts, which a group cannot hold. Everything else passes
 * through — including consumer components that render a `Menu.Item` themselves,
 * such as `TableActionsMenu.CopyItem`.
 */
const UNSUPPORTED_CHILD_TYPES: unknown[] = [
  Content,
  Trigger,
  Sub,
  SubContent,
  SubTrigger,
];

function existenceOperatorSymbol(operator: 'exists' | 'not-exists'): string {
  return operator === 'exists' ? '=' : '!=';
}

function addStatement(
  store: FilterCompositionStore,
  filterKey: string,
  filterValue: string | number | boolean,
  operator: OfferedOperator,
  filterType: FilterFieldPrimitive,
): void {
  // `FilterFieldValueOperationStatement` is a discriminated union whose
  // existence arm forbids both `value` and `valueType`, so the two shapes are
  // built separately.
  if (operator === 'exists' || operator === 'not-exists') {
    store.filterField.addFilter({
      key: filterKey,
      comparisonOperator: operator,
    });
    return;
  }

  // Without the type the written value is inferred from its content, which
  // contradicts the key's declared type whenever the two disagree — a
  // `filterType="String"` value of `42` would be stored as a Number and the
  // FilterField would reject it against its own validator map.
  store.filterField.addFilter({
    key: filterKey,
    value: filterValue,
    comparisonOperator: operator,
    valueType: filterType,
  });
}

/**
 * Serializes the statement the item would add into filter-language text, reusing
 * the FilterField's own `addFilter` so every operator renders exactly as the
 * FilterField would write it. Fed to {@link FilterStatement} for syntax
 * highlighting in the item label.
 */
function buildStatementString(
  filterKey: string,
  filterValue: string | number | boolean,
  operator: OfferedOperator,
  filterType: FilterFieldPrimitive,
  parserConfig: FilterFieldProps['parserConfig'],
): string {
  if (operator === 'exists' || operator === 'not-exists') {
    return addFilter('', {
      key: filterKey,
      comparisonOperator: operator,
    }).value;
  }

  // The same type hint and parser config `addStatement` writes with, so the
  // previewed statement is character-for-character the one the item adds.
  return addFilter(
    '',
    {
      key: filterKey,
      value: filterValue,
      comparisonOperator: operator,
      valueType: filterType,
    },
    { parserConfig },
  ).value;
}

/**
 * Renders one menu item per filter operation available for a key/value pair,
 * inside any `Menu` — a table cell menu, a chart context menu, an app-header
 * menu. Selecting an item adds the statement to a `FilterComposition`'s
 * FilterField; a single Remove item clears the key again.
 *
 * The composition is referenced by `filterCompositionId` rather than by context,
 * so the menu reaches it from anywhere in the app — portalled menus included.
 * Apply is not triggered: the statement lands in the FilterField and the
 * consumer applies as usual.
 *
 * @internal
 */
export const FilterCompositionMenuGroup = (
  props: FilterCompositionMenuGroupProps,
) => {
  const {
    filterCompositionId,
    filterKey,
    filterValue,
    filterType = 'Any',
    operators,
    children,
    className,
    style,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;
  const intl = useIntl();
  const ariaLabelingProps = useAriaLabelingProps(props);
  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);

  const store = useFilterCompositionById(filterCompositionId);
  const filterFieldValue = useFilterFieldValueOf(store);

  useEffect(() => {
    if (store === undefined) {
      // Names structural identifiers only — `filterValue` can be raw table-cell
      // or telemetry data and must never reach a log.
      devLog(
        `[${DISPLAY_NAME}] No FilterComposition is mounted with id "${filterCompositionId}". The items for key "${filterKey}" render disabled.`,
      );
    }
  }, [store, filterCompositionId, filterKey]);

  const offeredOperators = useMemo(() => {
    const allowed =
      getAllowedOperatorsForType(filterType).filter(isOfferedOperator);
    if (operators === undefined) {
      return allowed;
    }
    return operators
      .filter(isOfferedOperator)
      .filter((operator) => allowed.includes(operator));
  }, [filterType, operators]);

  const parserConfig = store?.filterField.parserConfig;

  // Statements for the key that carry a value of the declared type. Anything of
  // another type is not something an Add item of this `filterType` could have
  // written, so it must not disable one.
  //
  // `filterType` is the consumer's claim about the key — the InteractiveLogs
  // story takes it from the Grail field type of the column the cell sits in — and
  // it has to match how the key is declared in the FilterField's `validatorMap`.
  // A mismatch shows up as a menu item that writes a statement the FilterField
  // then flags as an invalid filter value.
  const typedMatches = useMemo(
    () =>
      store === undefined
        ? []
        : getFilters(
            filterFieldValue,
            { key: filterKey, valueType: filterType },
            { parserConfig },
          ),
    [store, filterFieldValue, filterKey, filterType, parserConfig],
  );

  // Every statement for the key, regardless of value type — the Remove item
  // clears the key with a key-only `removeFilter`, which is type-agnostic too.
  const keyMatches = useMemo(
    () =>
      store === undefined
        ? []
        : getFilters(filterFieldValue, { key: filterKey }),
    [store, filterFieldValue, filterKey],
  );

  // The value the declared type lowers to, compared as-is against the scalars
  // `getFilters` reports.
  const comparableValue = useMemo(
    () => toComparableScalar(filterValue, parserConfig, filterType),
    [filterValue, parserConfig, filterType],
  );

  const [groupChildren, unsupportedChildCount] = useMemo(() => {
    const supported: ReactNode[] = [];
    let unsupported = 0;
    Children.forEach(children, (child) => {
      if (
        isValidElement(child) &&
        UNSUPPORTED_CHILD_TYPES.includes(child.type)
      ) {
        unsupported += 1;
        return;
      }
      supported.push(child);
    });
    return [supported, unsupported] as const;
  }, [children]);

  useEffect(() => {
    if (unsupportedChildCount > 0) {
      devLog(
        `[${DISPLAY_NAME}] Menu container parts are not valid children of the filter group. ${unsupportedChildCount} child(ren) were ignored.`,
      );
    }
  }, [unsupportedChildCount]);

  const groupLabel = intl.formatMessage(messages.groupLabel);

  return (
    <Group
      {...ariaLabelingProps}
      aria-label={
        ariaLabelingProps['aria-labelledby'] === undefined
          ? (ariaLabelingProps['aria-label'] ?? groupLabel)
          : ariaLabelingProps['aria-label']
      }
      className={className}
      style={style}
      data-testid={dataTestId}
    >
      <Label aria-label={groupLabel}>{groupLabel}</Label>
      {offeredOperators.map((operator) => {
        const isExistence = operator === 'exists' || operator === 'not-exists';
        const displayValue = isExistence
          ? EXISTENCE_VALUE
          : String(filterValue);
        // `getOperatorShortName` renders the existence operators as `= *` / `!= *`,
        // which already carries the wildcard. The wildcard is shown as the value
        // instead, so the label reads `Filter by status = *` rather than `= * *`.
        const operatorSymbol = isExistence
          ? existenceOperatorSymbol(operator)
          : (getOperatorShortName(operator) ?? operator);
        const conflicting = CONFLICTING_OPERATOR[operator];
        const isPresent =
          store !== undefined &&
          isStatementPresent(typedMatches, operator, comparableValue);
        const isConflict =
          store !== undefined &&
          conflicting !== undefined &&
          isStatementPresent(typedMatches, conflicting, comparableValue);
        const disabled = store === undefined || isPresent || isConflict;
        // Which of the two disabled reasons applies drives the tooltip copy.
        // `present` wins if an item is somehow both applied and conflicting.
        const disabledTooltip = isPresent
          ? messages.alreadySelected
          : isConflict
            ? messages.conflictsWithSelection
            : undefined;
        const disabledReason =
          disabledTooltip === undefined
            ? undefined
            : intl.formatMessage(disabledTooltip);

        const operatorName = intl.formatMessage(operatorMessages[operator]);
        // The visible label is syntax-highlighted, so the accessible name is set
        // explicitly to a plain, deterministic statement plus the operator name
        // rather than the decorative highlighted spans.
        const statementLabel = intl.formatMessage(messages.filterByStatement, {
          statement: `${filterKey} ${operatorSymbol} ${displayValue}`,
        });
        const itemLabel = [statementLabel, operatorName, disabledReason]
          .filter((label) => label !== undefined)
          .join(' ');

        return (
          <Item
            key={operator}
            // Soft-disabled (not hard `disabled`) so the item stays focusable
            // and can reveal its why-disabled tooltip; `onSelect` is a no-op
            // while disabled and the menu stays open.
            aria-disabled={disabled}
            aria-label={itemLabel}
            textValue={itemLabel}
            onSelect={() => {
              if (disabled || store === undefined) {
                return;
              }
              addStatement(store, filterKey, filterValue, operator, filterType);
            }}
            data-dt-children-name="FilterCompositionMenuGroupOperator"
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            {...behaviorTrackingProps}
          >
            {disabledTooltip !== undefined && (
              <ItemTooltip>{disabledReason}</ItemTooltip>
            )}
            <Prefix>
              <FilterIcon />
            </Prefix>
            <span className={styles.label}>
              <FormattedMessage
                {...messages.filterByStatement}
                values={{
                  statement: (
                    <FilterStatement
                      value={buildStatementString(
                        filterKey,
                        filterValue,
                        operator,
                        filterType,
                        parserConfig,
                      )}
                      highlighting={disabled ? 'minimal' : 'syntax'}
                    />
                  ),
                }}
              />
            </span>
            <Suffix className={styles.suffix}>{operatorName}</Suffix>
          </Item>
        );
      })}
      {store !== undefined && keyMatches.length > 0 && (
        <Item
          textValue={intl.formatMessage(messages.removeFiltersForKey, {
            key: filterKey,
          })}
          onSelect={() =>
            store.filterField.removeFilter(
              { key: filterKey },
              { removeAll: true },
            )
          }
          data-dt-children-name="FilterCompositionMenuGroupRemove"
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          {...behaviorTrackingProps}
        >
          <Prefix>
            <XmarkIcon />
          </Prefix>
          <span className={styles.statement}>
            <FormattedMessage
              {...messages.removeFiltersForKey}
              values={{
                key: <span className={styles.emphasized}>{filterKey}</span>,
              }}
            />
          </span>
        </Item>
      )}
      {groupChildren}
    </Group>
  );
};

(
  FilterCompositionMenuGroup as typeof FilterCompositionMenuGroup & {
    displayName: string;
  }
).displayName = DISPLAY_NAME;
